// What is typed into a form that keeps nothing of its own (FR-UI-11):
// Review's and the plan review's reasons for a rejection, a dismissal's
// reasons, a flag dismissal's note, an approval's and a restore's note, the
// reasons Return to draft and Withdraw my vote give, the design notes,
// Redraft's instruction, Needs no spec's why and a bootstrap area's
// description and globs. Specter's pages are served `Cache-Control:
// no-store` (NFR-SEC-03), so Back fetches a page again rather than show
// the copy the browser kept, typing and all. So
// each box's unsent text is kept in the tab's session storage, per page
// and field, as the composer keeps its own (FR-EDIT-20), with what the box
// was served holding; it is put back when the page loads again, into a box
// served empty or as it was then (a refused reason the server brought back,
// or notes someone else saved meanwhile, are newer and stay), and forgotten
// when its form is sent. Sign out forgets all of it, so a shared computer
// keeps nothing. The editor, the composer, the exploration and the plan's
// own text keep theirs themselves; the editor's Note goes with its Save.

/** The fields kept, by name. */
const NAMES = [
  "reason",
  "notes",
  "note",
  "instruction",
  "description",
  "globs",
];

/** A box of one of those names left to its own form: the editor's Note, beside Save (FR-EDIT-27). */
const OWN_KEEPER = ":not(#save-note)";

/** Those fields, as boxes a person types into in a form that posts. */
export const KEPT_FIELDS = NAMES.flatMap((name) => [
  `form[method="post" i] textarea[name="${name}"]${OWN_KEEPER}`,
  `form[method="post" i] input[type="text"][name="${name}"]${OWN_KEEPER}`,
]).join(", ");

const PREFIX = "specter-form-unsent:";

type Box = HTMLTextAreaElement | HTMLInputElement;

function isBox(target: EventTarget | null): target is Box {
  return (
    (target instanceof HTMLTextAreaElement ||
      target instanceof HTMLInputElement) &&
    target.matches(KEPT_FIELDS) &&
    !target.readOnly &&
    !target.disabled
  );
}

/**
 * Where a box's text is kept: the page, the form's action, and the box by
 * its id, or, for one of several without one (a dismissal's reason beside
 * each note), by its name and the hidden fields that say what its form
 * is for.
 */
export function keyOf(box: Box): string | null {
  const form = box.form;
  if (form === null) return null;
  let which = box.id;
  if (which === "") {
    const hidden = Array.from(
      form.querySelectorAll<HTMLInputElement>('input[type="hidden"]'),
    )
      .filter((input) => input.name !== "" && input.name !== "_csrf")
      .map((input) => `${input.name}=${input.value}`);
    which = [box.name, ...hidden].join("&");
  }
  return `${PREFIX}${location.pathname}|${form.getAttribute("action") ?? ""}|${which}`;
}

/** A box's kept text, and what the box was served holding. */
interface Kept {
  text: string;
  served: string;
}

function keep(box: Box): void {
  const key = keyOf(box);
  if (key === null) return;
  try {
    if (box.value === box.defaultValue) sessionStorage.removeItem(key);
    else {
      const kept: Kept = { text: box.value, served: box.defaultValue };
      sessionStorage.setItem(key, JSON.stringify(kept));
    }
  } catch {
    // No storage here: the text goes as before, with the page.
  }
}

function keptIn(key: string): Kept | null {
  try {
    const kept: unknown = JSON.parse(sessionStorage.getItem(key) ?? "null");
    if (
      typeof kept === "object" &&
      kept !== null &&
      typeof (kept as Kept).text === "string" &&
      typeof (kept as Kept).served === "string"
    ) {
      return kept as Kept;
    }
  } catch {
    // Unreadable, or no storage: nothing to put back.
  }
  return null;
}

function forget(box: Box): void {
  const key = keyOf(box);
  if (key === null) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing was kept without storage.
  }
}

/** Forget every box's text this tab kept: Sign out. */
export function forgetAll(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    for (const key of keys) sessionStorage.removeItem(key);
  } catch {
    // Nothing was kept without storage.
  }
}

/**
 * Put each kept text back into its box when the page served the box empty
 * or as it was when the text was typed, opening the disclosure it is
 * folded in, so what came back is seen.
 */
export function restore(root: ParentNode = document): void {
  for (const box of root.querySelectorAll<Box>(KEPT_FIELDS)) {
    if (!isBox(box) || box.value !== box.defaultValue) continue;
    const key = keyOf(box);
    if (key === null) continue;
    const kept = keptIn(key);
    if (kept === null) continue;
    if (box.defaultValue !== "" && kept.served !== box.defaultValue) continue;
    box.value = kept.text;
    for (
      let details = box.closest("details");
      details !== null;
      details = details.parentElement?.closest("details") ?? null
    ) {
      details.open = true;
    }
    // Heard as typing is, by what counts or checks the box.
    box.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

document.addEventListener("input", (event) => {
  if (isBox(event.target)) keep(event.target);
});

// Heard last, on the window, so a submit something stopped (a question
// answered Cancel, a second press) keeps the text.
window.addEventListener("submit", (event) => {
  const form = event.target;
  if (event.defaultPrevented || !(form instanceof HTMLFormElement)) return;
  if (form.getAttribute("action") === "/-/logout") {
    forgetAll();
    return;
  }
  for (const box of form.querySelectorAll<Box>(KEPT_FIELDS)) {
    if (box.form === form) forget(box);
  }
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => restore());
} else {
  restore();
}
